import { useNavigate, useParams } from "react-router-dom"
import { MarkerImage, MarkerLocation, WalkNote, CompletedMarker } from "../../../../Interfaces"
import Map from "../../../../components/maps/Map"
import { useContext, useEffect, useRef, useState } from "react"
import DataContext from "../../../../context/dataProvider"
import { useQuery } from "@tanstack/react-query"
import { locationService } from "../../../../services/locationService"
import PopupModal from "../../../../components/modals/PopupModal"
import { walkService } from "../../../../services/walkService"
import { getTimeDifference } from "../../../../utils/helper"

export default function SingleWalkReport() {
  const navigate = useNavigate()
  const { id, walkReportId } = useParams()
  const { data: walkReport, isLoading, error } = useQuery(["walkReport", walkReportId], () => walkService.getOneReport(Number(walkReportId)), {
    enabled: walkReportId !== undefined,
  })

  console.log("Walk report query:", { walkReportId, isLoading, error, walkReport })

  const [modalContent, setModalContent] = useState<any>()
  const [isOpen, setIsOpen] = useState(false)
  const [mapModalContent, setMapModalContent] = useState<any>()
  const [isMapModalOpen, setIsMapModalOpen] = useState(false)
  const { currentLocation } = useContext(DataContext)
  const [markerImages, setMarkerImages] = useState<MarkerImage[]>([])
  const [markers, setMarkers] = useState<MarkerLocation[]>([])
  const isCurrentLocationEmpty = currentLocation && currentLocation.id !== walkReport?.walk?.locationId
  // This is only used if the page is refreshed and currentLocation is empty
  const { data } = useQuery(["currentLocation", walkReport?.walk?.locationId!], () => locationService.getOne(walkReport?.walk?.locationId!), { enabled: isCurrentLocationEmpty })
  const locationData = isCurrentLocationEmpty ? data : currentLocation
  const coordinates = useRef<google.maps.LatLngLiteral>({ lat: Number(locationData?.latitude || 0), lng: Number(locationData?.longitude || 0) })
  const [zoom, setZoom] = useState<number>(locationData?.zoomLevel || 18)

  // Initialize state when walkReport data is available
  useEffect(() => {
    if (walkReport) {
      console.log("Processing walk report data:", walkReport)
      setMarkerImages(walkReport.markerImage || [])

      // Handle markers - check for CompletedMarker first, then fall back to walk.markers
      let markerData: (MarkerLocation | CompletedMarker)[] = []

      if (walkReport.CompletedMarker && walkReport.CompletedMarker.length > 0) {
        console.log("Using CompletedMarker data:", walkReport.CompletedMarker)
        markerData = walkReport.CompletedMarker
      } else if (walkReport.walk?.markers && walkReport.walk.markers.length > 0) {
        console.log("Using walk.markers data:", walkReport.walk.markers)
        markerData = walkReport.walk.markers
      } else {
        console.log("No marker data found")
        markerData = []
      }

      setMarkers(markerData as MarkerLocation[])
    }
  }, [walkReport])

  useEffect(() => {
    // use walkService to get presigned urls for marker images while page loads. Assign them to the markerImage.url property
    if (!walkReport?.markerImage) return
    const markerImages = walkReport.markerImage.map(async (image) => {
      const presignedViewableUrl = await walkService.getViewableMarkerImage(image.walkReportId, image.markerId, image.imageName)
      image.imageUrl = presignedViewableUrl
      return image
    })
    Promise.all(markerImages).then((images) => {
      setMarkerImages(images)
    })
  }, [walkReport?.markerImage])

  useEffect(() => {
    if (currentLocation && locationData?.zoomLevel && markerImages && markers && walkReport?.walkNote) {
      const walkNotes = walkReport?.walkNote
      const mergedMarkers: MarkerLocation[] = markers?.map((marker) => {
        const notes = walkNotes?.filter((note) => note.markerId === marker.id)
        const images = markerImages?.filter((image) => image.markerId === marker.id)
        return {
          ...marker,
          notes,
          images,
        }
      })
        .sort((a, b) => a.label - b.label) || [];
      setMarkers(mergedMarkers);
      setZoom(locationData.zoomLevel || 18);
    }
  }, [currentLocation, locationData?.zoomLevel, markerImages, markers, walkReport?.walkNote])

  // Add loading state
  if (isLoading || !walkReport) {
    return (
      <div className="w-full min-h-full flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading walk report...</p>
        </div>
      </div>
    )
  }

  if (!walkReport || !walkReport.walk || !walkReport.walk.locationId || !locationData) return null

  const timeDifference = walkReport?.finishedAt ? getTimeDifference(new Date(walkReport?.startedAt), new Date(walkReport?.finishedAt)) : { hours: 0, minutes: 0, seconds: 0 }

  const totalWalkTime = Number(timeDifference.hours) + Number(timeDifference.minutes) + Number(timeDifference.seconds) > 0 && `${timeDifference.hours + ":" + timeDifference.minutes + ":" + timeDifference.seconds}`

  const finishedAt = walkReport?.finishedAt ? new Date(walkReport?.finishedAt).toLocaleTimeString() : "Not Completed"

  function handleNoteClick(notes: WalkNote[]) {
    if (!notes) return
    setModalContent(
      <div className="flex flex-wrap sm:rounded-lg p-1 bg-secondary-extra-light max-w-3xl text-lg">
        <div className="w-full flex flex-col overflow-auto max-h-[800px]">
          {notes.map((note: WalkNote) => (
            <ul key={note.id}>
              <li className="p-2 rounded-md shadow-md">{note.note}</li>
            </ul>
          ))}
        </div>
      </div>
    )
    setIsOpen(true)
  }

  function handleImageClick(images: MarkerImage[]) {
    if (!images) return

    setModalContent(
      <div className="flex flex-col overflow-auto max-h-[750px]">
        {images.map((image: MarkerImage) => (
          // contain images and make them scrollable if they are too big or there are too many
          <div className="object-contain h-full max-w-5xl" key={image.id}>
            {image.imageUrl ? (
              <img src={image.imageUrl} alt={image.imageName} />
            ) : (
              <div className=" h-72 w-full min-w-[400px] bg-slate-300 flex justify-center items-center px-4">
                <h2 className="font-bold text-center">No Image Available</h2>
              </div>
            )}
          </div>
        ))}
      </div>
    )
    setIsOpen(true)
  }

  function handleCloseModal() {
    setIsOpen(false)
    setModalContent(null)
  }

  function handleCloseMapModal() {
    setIsMapModalOpen(false)
    setMapModalContent(null)
  }

  function handleMarkerMapClick(marker: MarkerLocation) {
    if (!marker.lat || !marker.lng) return

    const markerCoordinates = { lat: marker.lat, lng: marker.lng }

    setMapModalContent(
      <div className="w-full h-full flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Marker {marker.label} Location
            {marker.unitNumberRange && (
              <span className="text-gray-600 ml-2">({marker.unitNumberRange})</span>
            )}
          </h3>
          <button
            onClick={handleCloseMapModal}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="flex-grow relative" style={{ height: '800px' }}>
          <Map
            name={""}
            zoom={18}
            setZoom={() => { }}
            markers={[marker]}
            coordinates={markerCoordinates}
            walkNotes={[]}
            walkImages={[]}
            handleNoteClick={() => { }}
            handleImageClick={() => { }}
            animate={false}
          />
        </div>
      </div>
    )
    setIsMapModalOpen(true)
  }

  return (
    <div className="w-full min-h-full flex justify-center">
      <div className="w-full flex flex-col">
        <section className="bg-secondary-extra-light p-2 md:rounded-lg mb-2 shadow-lg h-full">
          <div className="bg-primary-dark h-full flex flex-col">
            <div className=" text-white rounded-lg p-4 flex justify-between">
              <div className="flex">
                <div className="mr-6">
                  <h3 className=" font-extrabold">{walkReport.user.firstName + " " + walkReport.user.lastName}</h3>
                  <h4 className="">{walkReport.walk.name}</h4>
                  <h4 className="">Date: {new Date(walkReport.finishedAt || walkReport.createdAt).toLocaleDateString()}</h4>
                </div>
                <div>
                  <h3 className="">{" "}</h3>
                  <h4 className=" pr-2">Started: {new Date(walkReport.startedAt).toLocaleTimeString()}</h4>
                  <h4 className="">Finished: {finishedAt}</h4>
                  <h4 className="">
                    Total Time: {totalWalkTime || "Not Completed"}
                  </h4>
                </div>
              </div>

              <div>
                <div className="flex justify-end p-2">
                  <button className="rounded-lg p-2 ml-3 text-white text-center bg-primary border-sedondary hover:border-2 hover:border-primary-light" onClick={() => navigate(`/app/locations/${id}`)}>
                    Walk Reports
                  </button>
                </div>
              </div>
            </div>

            {/* Completed Markers List */}
            {walkReport.CompletedMarker && walkReport.CompletedMarker.length > 0 && (
              <div className="bg-white p-4 mx-4 mb-4 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Completed Markers</h3>
                <div className="space-y-2">
                  {walkReport.CompletedMarker
                    .map((completedMarker) => {
                      // Find the corresponding marker details from walk.markers
                      const markerDetails = walkReport.walk?.markers?.find(marker => marker.id === completedMarker.markerId)
                      // Find images for this marker
                      const markerImages = walkReport.markerImage?.filter(img => img.markerId === completedMarker.markerId) || []

                      return {
                        completedMarker,
                        markerDetails,
                        markerImages,
                        label: markerDetails?.label || completedMarker.markerId
                      }
                    })
                    .sort((a, b) => Number(a.label) - Number(b.label)) // Sort numerically
                    .map(({ completedMarker, markerDetails, markerImages, label }) => (
                      <div key={completedMarker.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center space-x-4">
                          <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
                            {label}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">
                              Marker {label}
                              {markerDetails?.unitNumberRange && (
                                <span className="text-gray-600 ml-2">({markerDetails.unitNumberRange})</span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600">
                              Arrived: {new Date(completedMarker.arrivedAt).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {/* Map Icon */}
                          {markerDetails && markerDetails.lat && markerDetails.lng && (
                            <button
                              onClick={() => handleMarkerMapClick(markerDetails)}
                              className="p-2 text-gray-600 hover:text-primary hover:bg-gray-100 rounded-full transition-colors"
                              title="View on map"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}

                          {/* Picture Match Status */}
                          {completedMarker.isDoor && markerImages.length > 0 && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-700">Picture:</span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Taken
                              </span>
                            </div>
                          )}

                          {/* Unit Match Status */}
                          {completedMarker.targetUnit && (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-700">Unit:</span>
                              {completedMarker.picturedUnit === completedMarker.targetUnit ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  ✓ Matched
                                </span>
                              ) : (
                                <div className="flex flex-col space-y-1">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    ⚠ Mismatch
                                  </span>
                                  <div className="text-xs text-gray-600">
                                    <div>Requested: {completedMarker.targetUnit}</div>
                                    <div>Detected: {completedMarker.picturedUnit || 'None'}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}

            <div className="flex flex-grow">
              <div className="shadow-lg  bg-secondary-extra-light w-full flex-grow relative">
                <Map name={""} zoom={zoom} setZoom={setZoom} markers={markers} coordinates={coordinates.current} walkNotes={walkReport.walkNote} walkImages={walkReport.markerImage} handleNoteClick={handleNoteClick} handleImageClick={handleImageClick} animate={true} />
              </div>
            </div>
          </div>
        </section>
      </div>
      <PopupModal isOpen={isOpen} onCancel={handleCloseModal} className={``}>
        {modalContent}
      </PopupModal>
      <PopupModal isOpen={isMapModalOpen} onCancel={handleCloseMapModal} className={`max-w-6xl w-full`}>
        {mapModalContent}
      </PopupModal>
    </div>
  )
}
